"use client";

import React, { JSX, useEffect, useTransition } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import cookie from "cookiejs";
import { useRouter } from "next/navigation";
import {
  GetUnlockCodeSchema,
  useGetUnlockCodeSchema,
} from "~/lib/client/schemas/unlock-code";
import {
  UNLOCK_CODE_COOKIE,
  UNLOCK_CODE_COOKIE_MAX_AGE,
} from "~/lib/definitions";
import { useTranslations } from "~/hooks/useTranslations";
import { Input } from "~/components/ui/input";
import { Button } from "~/components/ui/button";
import { ValidationError } from "~/components/ValidationError";
import { LoadingSpinner } from "~/components/LoadingSpinner";

interface IUnlockCodeContent {
  invalidUnlockCode?: boolean;
}

export function UnlockCodeContent({
  invalidUnlockCode,
}: Readonly<IUnlockCodeContent>): JSX.Element {
  const router = useRouter();
  const t = useTranslations();
  const unlockCode = cookie.get(UNLOCK_CODE_COOKIE);
  const [isPending, startTransition] = useTransition();

  const form = useForm<GetUnlockCodeSchema>({
    resolver: zodResolver(useGetUnlockCodeSchema()),
    defaultValues: { code: unlockCode ? unlockCode.toString() : "" },
  });

  const onSubmit = async ({ code }: GetUnlockCodeSchema) => {
    startTransition(() => {
      cookie.set(UNLOCK_CODE_COOKIE, code, {
        secure: true,
        expires: UNLOCK_CODE_COOKIE_MAX_AGE,
      });

      router.refresh();
    });
  };

  useEffect(() => {
    if (invalidUnlockCode) {
      cookie.remove(UNLOCK_CODE_COOKIE);
    }
  }, [invalidUnlockCode, router]);

  return (
    <div className="flex h-[275px] flex-col gap-6 p-4">
      <div className="text-lg font-bold">{t.unlock_code.unlock}</div>
      <p className="text-sm">{t.unlock_code.description}</p>

      <form
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Input
          autoFocus
          name="code"
          className="w-full"
          control={form.control}
          placeholder={t.unlock_code.enter_code}
        />

        <ValidationError
          display={!!invalidUnlockCode}
          message={t.error_messages.unlock_code_invalid}
        />

        <div>
          <Button
            type="submit"
            disabled={isPending}
            className="flex w-full flex-row gap-2"
          >
            {isPending && <LoadingSpinner width={8} height={8} />}
            <span>{t.unlock_code.unlock}</span>
          </Button>
        </div>
      </form>
    </div>
  );
}
